/**
 * A proposed card in the node modal: the sentence it stands on, and the three
 * taps — Pidä / Pehmennä / Hylkää (keep / soften / reject).
 *
 * A proposed node was drafted from the team's own documents
 * (scripts/agents/prefill-chain.mjs) and is not the team's statement until a
 * person says so (specs/workshop-modules/impact_path.md, "Proposed nodes").
 * Keep makes it active as drafted; soften makes it active with the person's
 * own wording; reject takes it off the canvas. Each tap can carry one line of
 * reason. Once decided, the panel shows only the provenance and the decision.
 */
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Check, PenLine, X } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation.jsx';
import API_ENDPOINTS from '../../config/api';

const Box = styled.div`
  margin: 14px 0 4px;
  padding: 12px 14px;
  border: 1px dashed rgba(255, 255, 255, 0.35);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
  color: #ddd;
  font-size: 0.9rem;
`;
const Kind = styled.div`
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
  margin-bottom: 6px;
`;
const Quote = styled.blockquote`
  margin: 0 0 6px;
  padding-left: 10px;
  border-left: 2px solid rgba(255, 255, 255, 0.25);
  font-style: italic;
  color: #ccc;
  white-space: pre-wrap;
`;
const Source = styled.div`
  font-size: 0.78rem;
  color: rgba(255, 255, 255, 0.5);
`;
const Row = styled.div`
  display: flex; gap: 8px; flex-wrap: wrap; align-items: center; margin-top: 12px;
`;
const Tap = styled.button`
  display: inline-flex; align-items: center; gap: 6px;
  padding: 8px 14px; border-radius: 999px; cursor: pointer;
  font: inherit; font-size: 0.86rem; font-weight: 600;
  color: ${(p) => p.$color}; background: transparent;
  border: 1px solid ${(p) => p.$color};
  &:hover { background: ${(p) => p.$color}22; }
  &:disabled { opacity: 0.45; cursor: default; }
`;
const Field = styled.input`
  flex: 1; min-width: 160px; padding: 8px 10px; border-radius: 8px;
  border: 1px solid rgba(255,255,255,0.15); background: rgba(255,255,255,0.04); color: #eee;
  font: inherit; font-size: 0.86rem;
  &:focus { outline: none; border-color: #a78bfa; }
`;
const Area = styled.textarea`
  width: 100%; min-height: 72px; margin-top: 10px; padding: 8px 10px; border-radius: 8px; resize: vertical;
  border: 1px solid rgba(255,255,255,0.15); background: rgba(255,255,255,0.04); color: #eee;
  font: inherit; font-size: 0.92rem;
  &:focus { outline: none; border-color: #a78bfa; }
`;
const Err = styled.div` color: #f87171; font-size: 0.82rem; margin-top: 8px; `;
const Decided = styled.div` margin-top: 8px; font-size: 0.82rem; color: rgba(255,255,255,0.6); `;

const COLORS = { keep: '#4ade80', soften: '#fbbf24', reject: '#f87171' };

export function Provenance({ provenance, t }) {
  if (!provenance?.quote) return null;
  const where = [provenance.source, provenance.round, provenance.date].filter(Boolean).join(' · ');
  return (
    <>
      <Kind>{t('impactPath.proposed.provenanceTitle')}</Kind>
      <Quote>{provenance.quote}</Quote>
      {where && <Source>{where}</Source>}
    </>
  );
}

/**
 * @param {object} props
 * @param {string} props.nodeId
 * @param {string} props.instanceId
 * @param {string} props.participantId
 * @param {string} props.status           'proposed' | 'active' | 'rejected'
 * @param {string} props.text             the card's current text (soften starts from it)
 * @param {object} [props.provenance]     { quote, source, round, date }
 * @param {object} [props.decision]       node_analysis.proposal once decided
 * @param {(result: object) => void} props.onDecided
 */
export default function ProposedNodePanel({ nodeId, instanceId, participantId, status, text, provenance, decision, onDecided }) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [softening, setSoftening] = useState(false);
  const [draft, setDraft] = useState(text || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const send = async (kind) => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      const r = await fetch(API_ENDPOINTS.DECIDE_PROPOSED_NODE(nodeId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          instanceId, participantId, decision: kind,
          reason: reason.trim() || undefined,
          ...(kind === 'soften' ? { text: draft.trim() } : {}),
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.success === false) throw new Error(data.error?.message || `HTTP ${r.status}`);
      onDecided?.(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (status !== 'proposed') {
    if (!provenance?.quote && !decision) return null;
    return (
      <Box>
        <Provenance provenance={provenance} t={t} />
        {decision && (
          <Decided>
            {t(`impactPath.proposed.decided.${decision.decision}`)}
            {decision.reason ? ` — ${decision.reason}` : ''}
          </Decided>
        )}
      </Box>
    );
  }

  return (
    <Box>
      <Kind>{t('impactPath.proposed.title')}</Kind>
      <div style={{ marginBottom: 8, color: 'rgba(255,255,255,0.75)' }}>{t('impactPath.proposed.lead')}</div>
      <Provenance provenance={provenance} t={t} />
      {softening && (
        <Area
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          aria-label={t('impactPath.proposed.softenLabel')}
          placeholder={t('impactPath.proposed.softenLabel')}
          maxLength={400}
        />
      )}
      <Row>
        <Field
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={t('impactPath.proposed.reasonPlaceholder')}
          aria-label={t('impactPath.proposed.reasonPlaceholder')}
          maxLength={200}
        />
      </Row>
      <Row>
        {!softening && status === 'proposed' && (
          <Tap type="button" $color={COLORS.keep} disabled={busy} onClick={() => send('keep')}>
            <Check size={14} /> {t('impactPath.proposed.keep')}
          </Tap>
        )}
        {softening ? (
          <>
            <Tap type="button" $color={COLORS.soften} disabled={busy || !draft.trim() || draft.trim() === (text || '').trim()} onClick={() => send('soften')}>
              <PenLine size={14} /> {t('impactPath.proposed.softenSave')}
            </Tap>
            <Tap type="button" $color="rgba(255,255,255,0.45)" disabled={busy} onClick={() => { setSoftening(false); setDraft(text || ''); }}>
              {t('common.cancel')}
            </Tap>
          </>
        ) : (
          <Tap type="button" $color={COLORS.soften} disabled={busy} onClick={() => setSoftening(true)}>
            <PenLine size={14} /> {t('impactPath.proposed.soften')}
          </Tap>
        )}
        {!softening && (
          <Tap type="button" $color={COLORS.reject} disabled={busy} onClick={() => send('reject')}>
            <X size={14} /> {t('impactPath.proposed.reject')}
          </Tap>
        )}
      </Row>
      {error && <Err>{error}</Err>}
    </Box>
  );
}
